import { apiUrl } from '@/lib/api-url';

const CSRF_COOKIE = 'arcadia_csrf';
const CSRF_HEADER = 'X-Arcadia-CSRF';

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const parts = document.cookie.split('; ');
  for (const p of parts) {
    const i = p.indexOf('=');
    if (i === -1) continue;
    const k = p.slice(0, i);
    if (k === name) return decodeURIComponent(p.slice(i + 1));
  }
  return null;
}

let csrfPrimed = false;
/** Set from `GET /api/auth/csrf` JSON when the CSRF cookie is not readable (cross-origin dev → deployed API). */
let crossOriginCsrfToken: string | null = null;

/** Fetches CSRF cookie from API (call once on app boot and after login). */
export async function ensureArcadiaCsrf(): Promise<void> {
  // Skip during SSR — there is no document to set cookies on.
  if (typeof document === 'undefined') return;
  const res = await fetch(resolveUrl('/api/auth/csrf'), { credentials: 'include' });
  csrfPrimed = res.ok;
  void csrfPrimed;
  if (res.ok) {
    try {
      const body = (await res.json()) as { csrfToken?: string };
      if (body.csrfToken && typeof body.csrfToken === 'string') {
        crossOriginCsrfToken = body.csrfToken;
      }
    } catch {
      /* ignore */
    }
  }
}

/**
 * Resolves a request URL relative to the API origin.
 *
 * On the client, leans on `apiUrl()` which uses `import.meta.env.VITE_API_URL`
 * (or same-origin when unset). In non-browser contexts (scripts, tests), we
 * prepend the configured internal API origin so `fetch()` gets an absolute URL.
 */
function resolveUrl(input: string | URL): string {
  if (typeof input !== 'string') return input.href;
  if (input.startsWith('http://') || input.startsWith('https://')) return input;
  const path = input.startsWith('/') ? input : `/${input}`;
  if (typeof window === 'undefined') {
    const base =
      process.env.PUBLIC_API_URL ||
      process.env.ARCADIA_INTERNAL_API_URL ||
      process.env.VITE_API_URL ||
      'http://127.0.0.1:4000';
    return `${base.replace(/\/$/, '')}${path}`;
  }
  return apiUrl(path);
}

function attachCsrf(headers: Headers, method: string): void {
  const m = method.toUpperCase();
  if (!['POST', 'PUT', 'PATCH', 'DELETE'].includes(m)) return;
  const token = crossOriginCsrfToken || readCookie(CSRF_COOKIE);
  if (token) headers.set(CSRF_HEADER, token);
}

/**
 * Fetch to the Arcadia API with cookies (session + CSRF).
 *
 * Dual-environment:
 * - Browser: same-origin `fetch` with `credentials: 'include'` (cookies attached
 *   automatically); CSRF token forwarded as a header.
 * - Non-browser: absolute URL via `resolveUrl()`. Optional: pass a `Cookie`
 *   header through `init.headers` — see `forwardSsrCookies()`.
 */
export async function apiFetch(input: string | URL, init?: RequestInit): Promise<Response> {
  const url = resolveUrl(input);
  const method = init?.method || 'GET';
  const headers = new Headers(init?.headers ?? undefined);
  attachCsrf(headers, method);
  return fetch(url, {
    ...init,
    headers,
    credentials: typeof window === 'undefined' ? init?.credentials : 'include',
  });
}

/**
 * Build a `Cookie` header to forward to the API when calling `apiFetch` outside
 * the browser (e.g. server scripts or tests).
 */
export function forwardSsrCookies(
  incomingHeaders: Record<string, string | string[] | undefined> | Headers | undefined
): Headers {
  const out = new Headers();
  if (!incomingHeaders) return out;
  let cookie: string | undefined;
  if (incomingHeaders instanceof Headers) {
    cookie = incomingHeaders.get('cookie') ?? undefined;
  } else {
    const v = incomingHeaders['cookie'] ?? incomingHeaders['Cookie' as keyof typeof incomingHeaders];
    cookie = Array.isArray(v) ? v.join('; ') : v;
  }
  if (cookie) out.set('cookie', cookie);
  return out;
}

/** Legacy name — no bearer token in cookie auth mode; kept for older call sites. */
export function getCachedAccessToken(): string | null {
  return null;
}
